import { Injectable, computed, signal } from '@angular/core';
import { SharedService } from '../shared-service/shared.service';
import { MENU_UI } from '../../constants/MenuKeys';
import { MenuPermission, MyPermissions, PERMISSION_FLAG, PermissionAction, SUPER_ADMIN_ROLE_ID } from '../../models/permission.model';

/** localStorage key of the signed-in admin's permissions; removed with the rest of the session on logout. */
const PERMISSIONS_STORAGE_KEY = 'permissions';

/**
 * Signal-based permission state of the signed-in admin.
 * Set from the login response, then kept current by PermissionRealtimeService (SignalR push), and stored
 * in localStorage so a reload renders immediately; never fetched over HTTP.
 * UI gating only: the API permission filter is the enforcement point.
 */
@Injectable({ providedIn: 'root' })
export class PermissionStore {
  private readonly _menus = signal<MenuPermission[]>([]);
  private readonly _roleId = signal<number | null>(null);
  private readonly _roleName = signal<string>('');
  private readonly _signedIn = signal<boolean>(false);

  readonly menus = this._menus.asReadonly();
  readonly roleId = this._roleId.asReadonly();
  readonly roleName = this._roleName.asReadonly();
  /** True while an admin session exists; drives the real-time connection. */
  readonly signedIn = this._signedIn.asReadonly();
  readonly isSuperAdmin = computed(() => this._roleId() === SUPER_ADMIN_ROLE_ID);

  private readonly byKey = computed(
    () => new Map(this._menus().map((menu) => [menu.menuKey.toLowerCase(), menu] as const))
  );

  constructor(private readonly sharedService: SharedService) {
    this.apply(this.readStored());
    // Sessions from before permissions were stored have none yet; the real-time snapshot fills them in.
    this._signedIn.set(!!this.sharedService.getValue('userId'));
    this.sharedService.storageCleared$.subscribe(() => this.clear());
  }

  /** Synchronous, signal-reactive check; safe in templates, guards and effects. */
  can(menuKey: string, action: PermissionAction = 'view'): boolean {
    const menu = this.byKey().get(menuKey.toLowerCase());
    if (!menu?.isView) {
      return false; // View first
    }
    return menu[PERMISSION_FLAG[action]] === true;
  }

  /** Only a Super Admin may modify the Super Admin role; nobody else may modify their own role (the API enforces both). */
  canModifyRole(roleId: number): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }
    return roleId !== SUPER_ADMIN_ROLE_ID && roleId !== this._roleId();
  }

  /** Stores permissions from the login response or a real-time update. */
  set(permissions: MyPermissions | null | undefined): void {
    const value = permissions ?? null;
    this.sharedService.setValue(PERMISSIONS_STORAGE_KEY, JSON.stringify(value));
    this.apply(value);
    this._signedIn.set(true);
  }

  /** First route the role may open; use after login instead of a hard-coded '/dashboard'. */
  firstAllowedRoute(): string | null {
    const menu = this._menus().find((m) => !m.isParent && m.isView && MENU_UI[m.menuKey]?.route);
    return menu ? MENU_UI[menu.menuKey].route! : null;
  }

  clear(): void {
    this.sharedService.removeValue(PERMISSIONS_STORAGE_KEY);
    this.apply(null);
    this._signedIn.set(false);
  }

  private apply(permissions: MyPermissions | null): void {
    this._menus.set(permissions?.menus ?? []);
    this._roleId.set(permissions?.roleId ?? null);
    this._roleName.set(permissions?.roleName ?? '');
  }

  private readStored(): MyPermissions | null {
    try {
      return JSON.parse(this.sharedService.getValue(PERMISSIONS_STORAGE_KEY) ?? 'null');
    } catch {
      return null;
    }
  }
}
